// src/features/system/developer/components/video-gallery/useVideoGallery.ts
// ────────────────────────────────────────────────────────────────────────────
// Hook-owns-all-state: fetch, cache, refetch cadence, filter state,
// derived visible-items, freshness, and lazy playback-url resolution.
//
// Components are pure renderers (zero props from the dashboard).
// ────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { backfillSidecars, deleteAllVideos, deleteVideo, fetchPlaybackUrl, fetchPosterUrl, fetchPreviewUrl, fetchVideoDetail, fetchVideoList, fetchVideoListStream, recoverVideo, runReconcileSweep, purgeOrphans, PlaybackNotReadyError, PosterNotReadyError, PreviewNotReadyError } from './api';
import type { ReconcileSingleResponse, ReconcileSweepResponse, PurgeOrphansResponse } from './api';
import type { BackfillSidecarsResponse, DeleteAllVideosResponse, DeleteVideoResponse, FeedBucket, PlaybackUrlResponse, PosterUrlResponse, PreviewUrlResponse, VideoDetailResponse, VideoFeedItem, VideoListClassification, VideoListResponse } from './types';
import { emitFeedEvent } from './telemetry';
import { normalizeFeedItem, referenceCreatedAtMs, toEpochMs } from './format';
import { fullJitter } from '../../../../../utils/retry';
import {
  readSnapshot,
  writeSnapshot,
  buildSnapshotKey,
} from '../../../../../utils/lastGoodSnapshot';

/** Empty classification — same shape as the BFF envelope when none was returned. */
const EMPTY_CLASSIFICATION: VideoListClassification = Object.freeze({
  orphan_count: 0,
  orphan_max_age_ms: 0,
  phantom_count: 0,
  phantom_max_age_ms: 0,
  ghost_count: 0,
  initiated_count: 0,
});

const QUERY_KEY = ['developer', 'video-gallery'] as const;
const REFETCH_INTERVAL_MS = 15_000;
const STALE_TIME_MS = 8_000;
const MAX_RETRY_ATTEMPTS = 3;

// ── Feature flag: NDJSON streaming list (P2.3) ──────────────────────
// Opt-in via `REACT_APP_VIDEO_LIST_STREAM=1`.  When enabled the hook
// consumes the BFF's `/list/stream` endpoint and paints the cached
// snapshot frame immediately (TTFB-critical) before the canonical
// SWR/cold compute settles, yielding the full payload via the
// `done`-terminated promise that React Query awaits.  When disabled
// (default) the legacy `fetchVideoList` JSON path is used.  Reversible
// at build time with zero runtime cost.
const USE_STREAMING_LIST: boolean =
  (typeof process !== 'undefined'
    && (process as { env?: Record<string, string | undefined> }).env?.REACT_APP_VIDEO_LIST_STREAM === '1');

const LIST_LIMIT = 2000;

function startOfLocalDayMs(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function isoWeekWindow(ms: number): { key: string; label: string; startMs: number; endMs: number } {
  const d = new Date(ms);
  const day = (d.getDay() + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday.getTime() + 7 * 86_400_000);

  const tmp = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate()));
  const dayNum = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);

  return {
    key: `${tmp.getUTCFullYear()}-W${String(week).padStart(2, '0')}`,
    label: `Week of ${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`,
    startMs: monday.getTime(),
    endMs: sunday.getTime(),
  };
}

function computeLocalBuckets(items: ReadonlyArray<VideoFeedItem>, nowMs = Date.now()): FeedBucket[] {
  const totalBytes = items.reduce((s, it) => s + (Number(it.byte_length) || 0), 0);
  const base: FeedBucket[] = [{
    key: 'all',
    label: 'All',
    count: items.length,
    totalBytes,
    startMs: 0,
    endMs: Number.MAX_SAFE_INTEGER,
  }];
  if (!items.length) return base;

  const dayMs = 86_400_000;
  const today = startOfLocalDayMs(nowMs);
  const yest = today - dayMs;

  const inRange = (it: VideoFeedItem, lo: number, hi: number) => {
    const createdAtMs = referenceCreatedAtMs(it);
    return createdAtMs >= lo && createdAtMs < hi;
  };
  const todayItems = items.filter((it) => inRange(it, today, today + dayMs));
  const yestItems = items.filter((it) => inRange(it, yest, today));

  if (todayItems.length) {
    base.push({
      key: `day-${today}`,
      label: 'Today',
      count: todayItems.length,
      totalBytes: todayItems.reduce((s, it) => s + (Number(it.byte_length) || 0), 0),
      startMs: today,
      endMs: today + dayMs,
    });
  }
  if (yestItems.length) {
    base.push({
      key: `day-${yest}`,
      label: 'Yesterday',
      count: yestItems.length,
      totalBytes: yestItems.reduce((s, it) => s + (Number(it.byte_length) || 0), 0),
      startMs: yest,
      endMs: today,
    });
  }

  const older = items.filter((it) => referenceCreatedAtMs(it) < yest);
  const byWeek = new Map<string, { label: string; startMs: number; endMs: number; count: number; totalBytes: number }>();
  for (const it of older) {
    const wk = isoWeekWindow(referenceCreatedAtMs(it));
    const existing = byWeek.get(wk.key);
    if (existing) {
      existing.count += 1;
      existing.totalBytes += Number(it.byte_length) || 0;
    } else {
      byWeek.set(wk.key, {
        label: wk.label,
        startMs: wk.startMs,
        endMs: wk.endMs,
        count: 1,
        totalBytes: Number(it.byte_length) || 0,
      });
    }
  }
  const weeks = Array.from(byWeek.entries())
    .sort((a, b) => b[1].startMs - a[1].startMs)
    .map(([k, v]) => ({
      key: `week-${k}`,
      label: v.label,
      count: v.count,
      totalBytes: v.totalBytes,
      startMs: v.startMs,
      endMs: v.endMs,
    }));

  return base.concat(weeks);
}

// ── localStorage-backed instant-paint persistence ───────────────────
// IG / YouTube / TikTok pattern: the last-good feed is mirrored to
// localStorage keyed by tenant + token + includeInitiated. On mount the
// hook hydrates React Query with the persisted snapshot so the FIRST
// paint after a hard refresh renders cards immediately while a fresh
// authoritative list is fetched in the background. Eliminates the
// "no videos yet” flash users hit after navigation/refresh.
//
// Bounded TTL = 24 h so we never hydrate a stale dataset that predates
// a delete/purge the user performed on another device. Server SWR +
// query.refetchOnMount immediately overwrites with the authoritative
// payload, so a stale persisted snapshot at worst causes ~1 s of
// outdated cards which is preferable to a blank screen.
//
// The persistence primitives live in `src/utils/lastGoodSnapshot.ts`
// so every feature hook that wants the same instant-paint affordance
// (operator-digest, sku-catalog, supplier, food-cost daily) inherits
// the same SSR-safe, quota-guarded, version-keyed semantics.
const LS_KEY_PREFIX = 'pac:video-gallery:v1';

function lsKey(token: string | null | undefined, includeInitiated: boolean): string {
  // Per-token (shared link or null = authenticated) + per-includeInitiated.
  // Tenant identity is baked into the BFF response (server-side filter)
  // and into the auth header, so a token → tenant collision is impossible.
  return buildSnapshotKey(
    LS_KEY_PREFIX,
    token ?? 'auth',
    includeInitiated ? 'all' : 'gallery',
  );
}

/** Validator: the persisted payload must look like a VideoListResponse. */
function isVideoListShape(data: unknown): boolean {
  return !!data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items);
}

function readPersisted(key: string) {
  return readSnapshot<VideoListResponse>(key, isVideoListShape);
}

function writePersisted(key: string, data: VideoListResponse, updatedAtMs: number): void {
  // Strip server-only telemetry to keep the payload small.
  const lean: VideoListResponse = { ...data };
  delete (lean as { served_from?: unknown }).served_from;
  delete (lean as { degraded?: unknown }).degraded;
  delete (lean as { retryAfterMs?: unknown }).retryAfterMs;
  writeSnapshot(key, lean, { updatedAtMs });
}

// Statuses that definitely lack sidecars yet — used to short-circuit
// hover-preview requests at the source so the network tab stays clean.
// Poster requests are NOT gated here: the worker writes poster much
// earlier in the pipeline and the lazy resolver already negative-caches.
const PREVIEW_PRESIDECAR_STATUSES = new Set([
  'initiated', 'uploaded', 'ingested', 'extracted',
]);

export type FreshnessState = 'live' | 'cached' | 'stale' | 'offline' | 'auth';

/** Recognise the auth-disconnect sentinel string thrown by fetch.ts. */
export function isAuthError(err: unknown): boolean {
  const msg = (err as Error | undefined)?.message ?? '';
  return /session expired/i.test(msg) || /unauthenticated/i.test(msg) || /401/.test(msg);
}

/**
 * Full-jitter exponential backoff (AWS Builder Library default) for FE
 * retries. Mirrors the orchestrator's `internal/retry` policy so client
 * and server behave consistently under transient stress.
 *
 *   attempt 0 → [0,   500]   ms
 *   attempt 1 → [0,  1000]   ms
 *   attempt 2 → [0,  2000]   ms
 *   attempt 3 → [0,  4000]   ms (capped @ 8000)
 *
 * Thin wrapper over the shared `fullJitter` primitive so every FE retry
 * curve is governed by one source of truth (see `utils/retry.ts`).
 */
export function retryDelayMs(attempt: number): number {
  return fullJitter(attempt, { baseMs: 500, maxMs: 8_000 });
}

/**
 * Skip retries on terminal failures so we don't burn budget hammering
 * an upstream we already know rejected us:
 *   • Auth / 401 — re-login required, retry would just 401 again.
 *   • Hard 4xx (non 408/429) — client error; idempotent retries won't
 *     change the verdict. 5xx, network errors, 408 (timeout) and 429
 *     (rate-limit) remain retryable.
 */
export function shouldRetryError(failureCount: number, err: unknown): boolean {
  if (failureCount >= MAX_RETRY_ATTEMPTS) return false;
  if (isAuthError(err)) return false;
  const msg = (err as Error | undefined)?.message ?? '';
  if (/\b4\d{2}\b/.test(msg) && !/\b408\b|\b429\b/.test(msg)) return false;
  return true;
}

export interface UseVideoGalleryOptions {
  /**
   * Public sharable-link token. When provided, all API calls (list,
   * playback URL minting, etc.) are routed through `/bff/shared/:token/*`
   * instead of `/bff/services/video-pipeline/*`. The hook also disables
   * write-side mutations server-side — the zero-trust guard rejects any
   * non-GET against a public link.
   */
  token?:    string;
  /**
   * Force read-only semantics on the FE. Implied true when `token` is
   * set. Hides every destructive affordance (delete, reconcile, delete-all)
   * regardless of permissions.
   */
  readOnly?: boolean;
}

// ── Client-side anti-shrink merge guard ─────────────────────────────
// YouTube/TikTok/IG/Spotify never let a feed visibly shrink due to a
// transient backend wobble. The BFF already runs a server-side
// `stale-empty-guard` / `stale-regress-guard` inside its SWR cache, but
// those guards are per-Cloud-Run-instance (in-memory) — a cold-start
// instance starts with an empty cache and CAN serve a smaller
// authoritative payload than the FE's prior snapshot. This guard makes
// that invisible to the user.
//
// Contract:
//   • The guard ONLY engages when the new payload is non-authoritative
//     (`served_from` ≠ `origin`/empty) OR when the new payload shrank
//     by more than `SHRINK_TOLERANCE_RATIO` items.
//   • Carried rows are tagged `_carried_from_snapshot = true` so the
//     renderer can dim or chip them as "syncing".
//   • The carried set is unioned by `video_id` — fresh rows always win
//     for ranking/order, carried rows append at the tail.
//   • The guard releases naturally as soon as a clean origin response
//     re-includes the carried rows (single refresh cycle).
//   • A legitimate user deletion goes through the mutation surface
//     which calls `qc.removeQueries()` → next list payload is treated
//     as authoritative even if smaller (see `purgePendingDeletionRef`
//     for the explicit-deletion intent signal).
//
// Why a separate hook: keeping the ref/merge logic outside
// `useVideoGallery` makes it unit-testable in isolation and keeps the
// main hook's cyclomatic complexity bounded.
const SHRINK_TOLERANCE_RATIO     = 0.85;  // tolerate ≤15% shrink between cycles
const SHRINK_MIN_PRIOR_FOR_GUARD = 8;     // never guard a tiny prior (genuine empty states)
const SHRINK_CARRY_MAX_AGE_MS    = 5 * 60 * 1000; // drop carries older than 5 min

interface AntiShrinkSnapshot {
  byId:        Map<string, VideoFeedItem>;
  count:       number;
  capturedMs:  number;
}

function isAuthoritativeServedFrom(servedFrom: string | undefined): boolean {
  if (!servedFrom) return true;             // BFF omitted the header = legacy origin
  return /^origin/.test(servedFrom);
}

function useAntiShrinkItems(data: VideoListResponse | undefined): VideoFeedItem[] {
  const snapshotRef = useRef<AntiShrinkSnapshot | null>(null);
  // Telemetry dedup: fire each list-level event AT MOST ONCE per
  // distinct server payload identity (data reference). Without this
  // guard the events would fire on every React render because
  // useMemo re-runs whenever any dep changes elsewhere in the tree.
  const lastEmittedDataRef = useRef<VideoListResponse | undefined>(undefined);
  const lastStaleServedFromRef = useRef<string | undefined>(undefined);

  return useMemo(() => {
    // Single normalization boundary: guarantee the numeric invariants
    // (created_at_ms / byte_length / duration_ms) for every item, no
    // matter the source (server digest, localStorage snapshot, optimistic
    // insert, stream merge). Downstream consumers — buckets, day windows,
    // groupByDay, search — rely on this contract and never re-defend.
    const fresh = (data?.items ?? []).map(normalizeFeedItem);
    const servedFrom = data?.served_from;
    const authoritative = isAuthoritativeServedFrom(servedFrom);
    const prior = snapshotRef.current;

    // Decide whether to engage the guard.
    const priorAgeMs = prior ? Date.now() - prior.capturedMs : Infinity;
    const priorEligible =
      !!prior &&
      prior.count >= SHRINK_MIN_PRIOR_FOR_GUARD &&
      priorAgeMs < SHRINK_CARRY_MAX_AGE_MS;

    const shrankNontrivially =
      priorEligible && fresh.length < prior!.count * SHRINK_TOLERANCE_RATIO;

    const nonAuthoritativeShrink =
      priorEligible && !authoritative && fresh.length < prior!.count;

    const engage = priorEligible && (shrankNontrivially || nonAuthoritativeShrink);

    // ── Telemetry (dedup'd to one emit per payload identity) ──
    const isNewPayload = data !== lastEmittedDataRef.current;
    if (isNewPayload && data !== undefined) {
      lastEmittedDataRef.current = data;

      // stale_served edge: emit only when the served_from value
      // transitions to a non-authoritative source (avoid flooding on
      // every stale poll).
      if (servedFrom && !authoritative && lastStaleServedFromRef.current !== servedFrom) {
        lastStaleServedFromRef.current = servedFrom;
        emitFeedEvent({
          kind: 'list_stale_served',
          detail: {
            served_from: servedFrom,
            count: fresh.length,
            prior_count: prior?.count ?? null,
          },
        });
      } else if (authoritative) {
        // Reset the dedup latch when we're back on origin so the next
        // stale episode emits again.
        lastStaleServedFromRef.current = undefined;
      }

      // shrink_detected: any time priorEligible && fresh < prior, even
      // when the guard ultimately doesn't carry (e.g. shrink within
      // tolerance + authoritative source). Surfaces backend variance
      // for SLO dashboards.
      if (priorEligible && fresh.length < prior!.count) {
        emitFeedEvent({
          kind: 'list_shrink_detected',
          detail: {
            count: fresh.length,
            prior_count: prior!.count,
            ratio: Math.round((fresh.length / prior!.count) * 1000) / 1000,
            served_from: servedFrom ?? null,
            authoritative,
            engaged: engage,
          },
        });
      }
    }

    if (!engage) {
      // Refresh the snapshot from any non-empty authoritative payload.
      // Non-authoritative payloads that match or exceed prior count
      // are also safe to record (they reflect either origin or a guard
      // that already returned the larger set).
      if (fresh.length > 0 && (authoritative || !prior || fresh.length >= prior.count)) {
        const byId = new Map<string, VideoFeedItem>();
        for (const it of fresh) byId.set(it.video_id, it);
        snapshotRef.current = { byId, count: fresh.length, capturedMs: Date.now() };
      }
      return fresh;
    }

    // Merge: fresh first (preserves server order/ranking), then carry
    // any video_id present in the prior snapshot but missing from fresh.
    const seen = new Set<string>();
    const merged: VideoFeedItem[] = [];
    for (const it of fresh) {
      seen.add(it.video_id);
      merged.push(it);
    }
    let carriedCount = 0;
    prior!.byId.forEach((it, id) => {
      if (seen.has(id)) return;
      merged.push({ ...it, _carried_from_snapshot: true });
      carriedCount += 1;
    });

    if (isNewPayload && carriedCount > 0) {
      emitFeedEvent({
        kind: 'list_carry_engaged',
        detail: {
          carried: carriedCount,
          fresh_count: fresh.length,
          merged_count: merged.length,
          prior_count: prior!.count,
          served_from: servedFrom ?? null,
          authoritative,
        },
      });
    }

    return merged;
  }, [data]);
}

export function useVideoGallery(options: UseVideoGalleryOptions = {}) {
  const { token, readOnly: readOnlyOpt } = options;
  const readOnly = !!(readOnlyOpt || token);
  const qc = useQueryClient();
  const mediaCacheScope = token ?? '__auth__';

  // ── UI state ──────────────────────────────────────────────────────
  const [includeInitiated, setIncludeInitiated] = useState(false);
  const [activeBucketKey,  setActiveBucketKey]  = useState<string>('all');
  const [searchText,       setSearchText]       = useState('');
  // Day-jump filter (epoch ms at local 00:00). When non-null, supersedes
  // the bucket filter and pins the visible feed to a single calendar day —
  // the affordance powered by the floating calendar button. Null means
  // "use bucket filter" (default behaviour).
  const [selectedDayMs, setSelectedDayMs] = useState<number | null>(null);

  // ── Server query ──────────────────────────────────────────────────
  // Caching contract (TikTok/IG/Shorts/YouTube-grade smoothness):
  //   • `placeholderData: keepPreviousData` — when re-fetching or
  //     toggling `includeInitiated`, the previous items remain visible
  //     until the new payload lands. NEVER blank-flash the gallery.
  //     The BFF mirrors this server-side via SWR + ETag + 304.
  //   • `refetchOnReconnect: 'always'` — after offline→online (mobile
  //     subway / spotty wifi), trigger an authoritative refresh.
  //   • `refetchIntervalInBackground: false` (default) — pause polling
  //     when the tab is hidden, saving battery + BFF load.
  //   • `retry` — skip auth and hard-4xx errors (no point hammering);
  //     bound to 3 attempts; full-jitter delay mirrors backend retry.
  const query = useQuery<VideoListResponse, Error>({
    queryKey: [...QUERY_KEY, { includeInitiated, token: token ?? null }],
    // First-page size lowered from 1000 → 200 (P1.4). Empirically a
    // viewport renders 6–12 cards; 200 rows comfortably cover the
    // initial fold + several scroll pages while halving the wire +
    // BFF hydration cost on every poll. Cursor-driven lazy pages
    // (`useInfiniteQuery` migration) land in Phase 2 alongside the
    // NDJSON streaming endpoint — switching the data-shape now would
    // ripple through ~6 downstream consumers and balloon the P1 blast
    // radius. The BFF already exposes `next_cursor` for the eventual
    // upgrade. Until then a tenant with > 200 videos sees the most
    // recent 200; older content remains reachable via the existing
    // calendar-jump affordance which queries on demand.
    //
    // When REACT_APP_VIDEO_LIST_STREAM=1, the queryFn consumes the
    // NDJSON /list/stream endpoint instead.  Early `chunk(phase:'cache')`
    // frames are painted via `qc.setQueryData` so the gallery flips to
    // its cached snapshot in <80 ms while the canonical compute settles
    // in the background.  The promise still resolves with the final
    // payload so every downstream React Query primitive (staleTime,
    // dataUpdatedAt, refetchOnReconnect) keeps working unchanged.
    queryFn: () => {
      if (!USE_STREAMING_LIST) {
        return fetchVideoList({ limit: LIST_LIMIT, includeInitiated, view: 'index', token });
      }
      const key = [...QUERY_KEY, { includeInitiated, token: token ?? null }] as const;
      return fetchVideoListStream({
        limit: LIST_LIMIT,
        includeInitiated,
        view:  'index',
        token,
        onCache: (cached) => {
          // Instant paint: hydrate the cache snapshot into React Query
          // before the final compute lands.  `dataUpdatedAt` is set to
          // a value just slightly older than the configured staleTime
          // so the eventual `final` resolution still counts as "fresh"
          // and overwrites without flicker.
          qc.setQueryData<VideoListResponse>(key as unknown as readonly unknown[], cached, {
            updatedAt: Date.now() - STALE_TIME_MS,
          });
        },
      });
    },
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
    retry: shouldRetryError,
    retryDelay: retryDelayMs,
    // Instant-paint after hard refresh: hydrate from the per-tenant
    // localStorage snapshot if present. `initialDataUpdatedAt` is set
    // to the snapshot's persisted timestamp so React Query honours
    // `staleTime` correctly — a snapshot older than 8 s triggers an
    // immediate background refetch (the user sees the persisted cards
    // while the wire request lands).
    initialData: () => {
      const snap = readPersisted(lsKey(token ?? null, includeInitiated));
      return snap?.data;
    },
    initialDataUpdatedAt: () => {
      const snap = readPersisted(lsKey(token ?? null, includeInitiated));
      return snap?.updatedAtMs;
    },
  });

  // Mirror the authoritative response to localStorage on every fresh
  // landing. We persist only after `isSuccess` so we never overwrite
  // a good snapshot with an in-flight placeholder. Keying on
  // `dataUpdatedAt` makes the write fire exactly once per fetch cycle.
  useEffect(() => {
    if (!query.isSuccess || !query.data) return;
    writePersisted(
      lsKey(token ?? null, includeInitiated),
      query.data,
      query.dataUpdatedAt || Date.now(),
    );
  }, [query.isSuccess, query.data, query.dataUpdatedAt, token, includeInitiated]);

  const items          = useAntiShrinkItems(query.data);
  const buckets        = useMemo(() => computeLocalBuckets(items), [items]);
  const classification = useMemo<VideoListClassification>(
    () => query.data?.classification ?? EMPTY_CLASSIFICATION,
    [query.data],
  );

  // Reset selection if the active bucket disappeared after refetch
  useEffect(() => {
    if (!buckets.length) return;
    if (!buckets.some((b) => b.key === activeBucketKey)) setActiveBucketKey('all');
  }, [buckets, activeBucketKey]);

  // ── Derived visible items (day OR bucket filter + free-text search) ─
  //
  // Ordering contract:
  //   • Default (bucket or all)  → newest first (DESC).
  //     Mirrors TikTok/Shorts/IG behaviour: the latest content is
  //     what users come back for.
  //   • Day jump (calendar pick) → oldest first (ASC, morning → night).
  //     Inside a single chosen day the user is doing time-travel — they
  //     want to relive the day chronologically (morning shifts → closing
  //     time), not in reverse. This is the same convention Photos /
  //     Google Photos apply when you open a day from the calendar view.
  const visibleItems = useMemo<VideoFeedItem[]>(() => {
    let lo: number;
    let hi: number;
    if (selectedDayMs != null) {
      // Day-jump filter takes priority over bucket. Lock window to the
      // local-calendar day [00:00, 00:00+24h).
      lo = selectedDayMs;
      hi = selectedDayMs + 24 * 60 * 60 * 1000;
    } else {
      const bucket = buckets.find((b) => b.key === activeBucketKey);
      lo = bucket?.startMs ?? 0;
      hi = bucket?.endMs   ?? Number.MAX_SAFE_INTEGER;
    }
    const q = searchText.trim().toLowerCase();

    const filtered = items.filter((it) => {
      const createdAtMs = referenceCreatedAtMs(it);
      if (!(createdAtMs >= lo && createdAtMs < hi)) return false;
      if (!q) return true;
      const displayName = (it.display_name ?? '').toLowerCase();
      return (
        displayName.includes(q) ||
        it.filename.toLowerCase().includes(q) ||
        it.status.toLowerCase().includes(q) ||
        it.sha256_short.toLowerCase().includes(q) ||
        (it.transcript_preview ?? '').toLowerCase().includes(q)
      );
    });

    // Day-jump → ASC (oldest first). Otherwise keep the upstream DESC
    // order untouched (the BFF sorts by created_at_ms DESC).
    if (selectedDayMs != null) {
      return [...filtered].sort(
        (a, b) => referenceCreatedAtMs(a) - referenceCreatedAtMs(b),
      );
    }
    return filtered;
  }, [items, buckets, activeBucketKey, searchText, selectedDayMs]);

  // ── Days with at least one video (sorted ASC, local-calendar 00:00 ms) ─
  // Powers the CalendarJumpButton — only days present in this set are
  // navigable. The set is stable across re-filters because it depends
  // ONLY on the full `items` payload, not on the active bucket / search.
  const daysWithVideos = useMemo<number[]>(() => {
    const set = new Set<number>();
    for (const it of items) {
      const ms = referenceCreatedAtMs(it);
      if (ms <= 0) continue;
      const d = new Date(ms);
      d.setHours(0, 0, 0, 0);
      set.add(d.getTime());
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [items]);

  // ── Freshness badge state ────────────────────────────────────────
  // Distinguish *why* we have (or don't have) fresh data:
  //   • auth    → session/credential problem (re-login fixes it)
  //   • offline → genuine transport/server failure (no data at all)
  //   • stale   → we have data but it's past staleTime and not refetching
  //   • live    → request currently in-flight, NO data to render yet
  //   • cached  → data on hand (fresh OR placeholder from prior fetch)
  //
  // With `placeholderData: keepPreviousData`, `query.data` carries the
  // prior payload during a refetch — we surface that as `cached` (not
  // `live`) because the user IS looking at server data, just not the
  // very latest. Matches YT/IG/TikTok semantics: feed never blanks out;
  // the freshness chip flips to indicate background refresh.
  const freshness: FreshnessState = useMemo(() => {
    if (query.isError && !query.data) {
      return isAuthError(query.error) ? 'auth' : 'offline';
    }
    if (!query.data) return 'live';
    const servedFrom = query.data.served_from;
    // Server-side guard fired → we're looking at a snapshot that the
    // BFF rescued (origin returned empty/regressed). Surface as 'stale'
    // so the user gets the "syncing" badge instead of "live".
    if (servedFrom && !isAuthoritativeServedFrom(servedFrom)) return 'stale';
    if (query.isStale && !query.isFetching) return 'stale';
    return 'cached';
  }, [query.isError, query.isFetching, query.isStale, query.data, query.error]);

  // True when any client-side carry is currently in `items`. Renderers
  // can use this to surface a single header-level "Re-syncing…" chip in
  // addition to the per-card freshness dot.
  const degraded = useMemo(() => {
    const servedFrom = query.data?.served_from;
    if (servedFrom && !isAuthoritativeServedFrom(servedFrom)) return true;
    return items.some((it) => (it as any)._carried_from_snapshot === true);
  }, [items, query.data]);

  // ── Lazy signed playback url (cached in React-Query per videoId) ─
  // One in-flight playback-url request per video_id to prevent burst traffic
  // when the preview modal re-renders while the first request is still pending.
  const inFlightPlayback = useRef<Map<string, Promise<PlaybackUrlResponse>>>(new Map());
  const inFlightDetail = useRef<Map<string, Promise<VideoDetailResponse>>>(new Map());

  /**
   * Negative cache for `/playback-url` 404s. Keyed by videoId, value =
   * `Date.now()` of last not-ready response. Read path checks this
   * BEFORE issuing a fetch; if the entry is younger than
   * [PLAYBACK_NEGATIVE_TTL_MS] we re-throw the cached sentinel synchronously
   * without touching the network.
   *
   * Why: under feed scroll, a slide can request playback up to
   * `prewarm + activate + recovery-retry + visibility-restore` times in
   * a few seconds. With a 60s post-upload materialisation window, a
   * single not-ready video would otherwise generate ~5 BFF round trips
   * per scroll-back. The negative cache collapses those into 1 fetch
   * per [PLAYBACK_NEGATIVE_TTL_MS] window, while still allowing organic
   * heal: the slide-level retry loop bypasses this cache by directly
   * deleting the key when its scheduled retry timer fires.
   */
  const negativePlaybackCache = useRef<Map<string, { atMs: number; err: PlaybackNotReadyError }>>(new Map());
  // Keep the negative memo shorter than the retry floor so scheduled
  // retries actually re-hit the BFF instead of short-circuiting forever.
  const PLAYBACK_NEGATIVE_TTL_MS = 2_000;
  // Poster sidecars are best-effort — the worker may not have written
  // them yet (faststart still in-flight, or 404'd for an unsupported
  // container). Cache negative responses for a bounded window so an
  // entire feed-of-non-posters does not generate one BFF round trip
  // per card on every render.
  const inFlightPoster = useRef<Map<string, Promise<PosterUrlResponse | null>>>(new Map());
  const negativePosterCache = useRef<Map<string, number>>(new Map());
  const POSTER_NEGATIVE_TTL_MS = 60_000;

  // Preview-clip sidecars are even more best-effort than posters: an
  // entire feed can legitimately have zero previews for the first few
  // minutes after a bulk upload while the worker chews through the
  // backlog. Same three-layer caching shape as the poster path so
  // hover-to-preview never DDoSes the BFF.
  const inFlightPreview = useRef<Map<string, Promise<PreviewUrlResponse | null>>>(new Map());
  const negativePreviewCache = useRef<Map<string, number>>(new Map());
  const PREVIEW_NEGATIVE_TTL_MS = 60_000;

  // ── Lazy-heal background retries ─────────────────────────────────
  // When the BFF returns 202 (sidecar generation dispatched), we keep
  // a per-videoId retry token so:
  //   • duplicate 202s from re-mounts of the same card don't fan out
  //     into N parallel retry loops (single-flight).
  //   • cleanup on unmount cancels pending retries.
  //
  // Backoff strategy: caps at MAX_LAZY_RETRIES with exponential base
  // delay derived from the server's `retry_after_ms` hint, plus ±20%
  // jitter to break thundering-herd alignment when a feed scrolled
  // past hundreds of fresh videos at once. Mirrors the AWS SDK retry
  // policy and the YouTube/IG client backoff conventions.
  const lazyRetryTokens = useRef<Map<string, { cancelled: boolean }>>(new Map());
  const MAX_LAZY_RETRIES = 4;          // 4 attempts × ~jittered base ≈ ≤45s
  const LAZY_JITTER_RATIO = 0.2;       // ±20%

  // Keep the share-link token reachable from inside retry loops without
  // re-creating schedulers on every render. Refs are stable identities.
  const tokenRef = useRef<string | undefined>(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  const jittered = useCallback((baseMs: number): number => {
    const j = baseMs * LAZY_JITTER_RATIO;
    return Math.max(500, Math.round(baseMs + (Math.random() * 2 - 1) * j));
  }, []);

  /**
   * Background self-heal retry loop. Fires AFTER `requestPoster`
   * resolves to `null` so the card has already painted its fallback.
   * On success, publishes via `qc.setQueryData` AND a window CustomEvent
   * `pac:poster:resolved` so already-mounted cards rebind without
   * forcing them to use react-query subscriptions.
   */
  const scheduleLazyPosterRetry = useCallback(
    (videoId: string, initialDelayMs: number) => {
      if (!videoId) return;
      if (lazyRetryTokens.current.has(`poster:${videoId}`)) return;
      const cancelToken = { cancelled: false };
      lazyRetryTokens.current.set(`poster:${videoId}`, cancelToken);

      const loop = async (attempt: number, delayMs: number): Promise<void> => {
        if (cancelToken.cancelled) return;
        await new Promise((res) => setTimeout(res, jittered(delayMs)));
        if (cancelToken.cancelled) return;
        try {
          const fresh = await fetchPosterUrl(videoId, { token: tokenRef.current });
          const ttlMs = (fresh.expires_in * 1000) * 0.9;
          qc.setQueryData(['video-poster', mediaCacheScope, videoId], {
            ...fresh,
            __expires_at: Date.now() + ttlMs,
          } as any);
          negativePosterCache.current.delete(videoId);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('pac:poster:resolved', {
              detail: { videoId, posterUrl: fresh.poster_url, attempt },
            }));
          }
          lazyRetryTokens.current.delete(`poster:${videoId}`);
        } catch (err) {
          if (cancelToken.cancelled) return;
          if (err instanceof PosterNotReadyError && attempt < MAX_LAZY_RETRIES) {
            const nextBase = err.retryAfterMs ?? delayMs * 2;
            return loop(attempt + 1, nextBase);
          }
          negativePosterCache.current.set(videoId, Date.now());
          lazyRetryTokens.current.delete(`poster:${videoId}`);
        }
      };

      void loop(1, initialDelayMs);
    },
    [mediaCacheScope, qc, jittered],
  );

  const scheduleLazyPreviewRetry = useCallback(
    (videoId: string, initialDelayMs: number) => {
      if (!videoId) return;
      if (lazyRetryTokens.current.has(`preview:${videoId}`)) return;
      const cancelToken = { cancelled: false };
      lazyRetryTokens.current.set(`preview:${videoId}`, cancelToken);

      const loop = async (attempt: number, delayMs: number): Promise<void> => {
        if (cancelToken.cancelled) return;
        await new Promise((res) => setTimeout(res, jittered(delayMs)));
        if (cancelToken.cancelled) return;
        try {
          const fresh = await fetchPreviewUrl(videoId, { token: tokenRef.current });
          const ttlMs = (fresh.expires_in * 1000) * 0.9;
          qc.setQueryData(['video-preview', mediaCacheScope, videoId], {
            ...fresh,
            __expires_at: Date.now() + ttlMs,
          } as any);
          negativePreviewCache.current.delete(videoId);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('pac:preview:resolved', {
              detail: { videoId, previewUrl: fresh.preview_url, attempt },
            }));
          }
          lazyRetryTokens.current.delete(`preview:${videoId}`);
        } catch (err) {
          if (cancelToken.cancelled) return;
          if (err instanceof PreviewNotReadyError && attempt < MAX_LAZY_RETRIES) {
            const nextBase = err.retryAfterMs ?? delayMs * 2;
            return loop(attempt + 1, nextBase);
          }
          negativePreviewCache.current.set(videoId, Date.now());
          lazyRetryTokens.current.delete(`preview:${videoId}`);
        }
      };

      void loop(1, initialDelayMs);
    },
    [mediaCacheScope, qc, jittered],
  );

  // Cancel all pending retries on unmount to prevent setState-after-unmount
  // and stop background traffic when the user navigates away.
  useEffect(() => {
    const tokens = lazyRetryTokens.current;
    return () => {
      tokens.forEach((t) => {
        t.cancelled = true;
      });
      tokens.clear();
    };
  }, []);

  const requestDetail = useCallback(
    async (videoId: string): Promise<VideoDetailResponse> => {
      const cached = qc.getQueryData<VideoDetailResponse>(['video-detail', mediaCacheScope, videoId]);
      if (cached) return cached;

      const pending = inFlightDetail.current.get(videoId);
      if (pending) return pending;

      const run = fetchVideoDetail(videoId, { token })
        .then((fresh) => {
          qc.setQueryData(['video-detail', mediaCacheScope, videoId], fresh);
          return fresh;
        })
        .finally(() => {
          inFlightDetail.current.delete(videoId);
        });

      inFlightDetail.current.set(videoId, run);
      return run;
    },
    [mediaCacheScope, qc, token],
  );

  const requestPlayback = useCallback(
    async (videoId: string): Promise<PlaybackUrlResponse> => {
      // Cache hit?
      const cached = qc.getQueryData<PlaybackUrlResponse>(['video-playback', mediaCacheScope, videoId]);
      if (cached && Date.now() < (cached as any).__expires_at) return cached;

      // Negative cache hit — re-throw the typed sentinel without a
      // round trip. The slide-level retry timer is the authoritative
      // pacer; this cache only protects against scroll-storm requests
      // happening WITHIN one retry window.
      const neg = negativePlaybackCache.current.get(videoId);
      if (neg && Date.now() - neg.atMs < PLAYBACK_NEGATIVE_TTL_MS) {
        throw neg.err;
      }

      const pending = inFlightPlayback.current.get(videoId);
      if (pending) return pending;

      const run = fetchPlaybackUrl(videoId, { token })
        .then((fresh) => {
          // Stash with absolute expiry (90% of TTL to be safe)
          const ttlMs = (fresh.expires_in * 1000) * 0.9;
          qc.setQueryData(['video-playback', mediaCacheScope, videoId], {
            ...fresh,
            __expires_at: Date.now() + ttlMs,
          } as any);
          // Healed — drop any stale not-ready memo so the next eviction
          // of the positive cache doesn't bounce back to the negative path.
          negativePlaybackCache.current.delete(videoId);
          return fresh;
        })
        .catch((err) => {
          if (err instanceof PlaybackNotReadyError) {
            negativePlaybackCache.current.set(videoId, { atMs: Date.now(), err });
          }
          throw err;
        })
        .finally(() => {
          inFlightPlayback.current.delete(videoId);
        });

      inFlightPlayback.current.set(videoId, run);
      return run;
    },
    [mediaCacheScope, qc, token],
  );

  /**
   * Lazy poster-url resolver. Returns `null` when the BFF reports
   * `poster_not_ready` / `poster_not_found` so the caller can render
   * the static placeholder without a try/catch boilerplate at every
   * tile. Throws only on genuine errors (5xx, network).
   *
   * Cache layers:
   *   1. React-Query positive cache, TTL = 90% of `expires_in`.
   *   2. In-flight Promise map: collapses bursts (re-mounting tiles
   *      during scroll) into a single round trip.
   *   3. Short-lived negative memo: 60 s window so a feed where every
   *      poster is missing doesn't re-fire one request per card on
   *      every scroll-back.
   */
  const requestPoster = useCallback(
    async (videoId: string): Promise<PosterUrlResponse | null> => {
      if (!videoId) return null;

      const cached = qc.getQueryData<PosterUrlResponse>(['video-poster', mediaCacheScope, videoId]);
      if (cached && Date.now() < (cached as any).__expires_at) return cached;

      const negAt = negativePosterCache.current.get(videoId);
      if (negAt && Date.now() - negAt < POSTER_NEGATIVE_TTL_MS) return null;

      const pending = inFlightPoster.current.get(videoId);
      if (pending) return pending;

      const run = fetchPosterUrl(videoId, { token })
        .then((fresh) => {
          const ttlMs = (fresh.expires_in * 1000) * 0.9;
          qc.setQueryData(['video-poster', mediaCacheScope, videoId], {
            ...fresh,
            __expires_at: Date.now() + ttlMs,
          } as any);
          negativePosterCache.current.delete(videoId);
          return fresh as PosterUrlResponse | null;
        })
        .catch((err) => {
          if (err instanceof PosterNotReadyError) {
            negativePosterCache.current.set(videoId, Date.now());
            // 202 lazy-heal path: BFF told us the sidecar was just
            // dispatched (or recently). Kick off a background retry
            // loop so the card eventually picks up the URL via the
            // `pac:poster:resolved` window event. `retryAfterMs` is
            // the server's hint; we jitter on top of it.
            if (err.retryAfterMs !== undefined) {
              scheduleLazyPosterRetry(videoId, err.retryAfterMs);
            }
            return null;
          }
          throw err;
        })
        .finally(() => {
          inFlightPoster.current.delete(videoId);
        });

      inFlightPoster.current.set(videoId, run);
      return run;
    },
    [mediaCacheScope, qc, token],
  );

  /**
   * Lazy preview-url resolver. Mirrors `requestPoster` exactly:
   *   1. React-Query positive cache, TTL = 90 % of `expires_in`.
   *   2. In-flight Promise map collapses bursts (rapid hover-on/off).
   *   3. Short-lived negative memo (60 s) so a feed where every
   *      preview is missing doesn't re-fire one request per hover.
   *
   * Returns `null` (not throw) when the BFF reports
   * `preview_not_ready` / `preview_not_found`. The card branches on
   * null and keeps the poster visible without try/catch boilerplate.
   */
  const requestPreview = useCallback(
    async (videoId: string): Promise<PreviewUrlResponse | null> => {
      if (!videoId) return null;

      // Status-gated short-circuit: the worker writes the preview
      // sidecar at the tail of the faststart pipeline (post-transcribe).
      // Asking for it before then is guaranteed to 404 — skip the round
      // trip entirely. Cards still render their poster on hover; the
      // negative cache below catches transient races and unknown
      // statuses fall through to the normal probe.
      const it = items.find((x) => x.video_id === videoId);
      const status = String(it?.status || '').toLowerCase();
      if (status && PREVIEW_PRESIDECAR_STATUSES.has(status)) {
        return null;
      }

      const cached = qc.getQueryData<PreviewUrlResponse>(['video-preview', mediaCacheScope, videoId]);
      if (cached && Date.now() < (cached as any).__expires_at) return cached;

      const negAt = negativePreviewCache.current.get(videoId);
      if (negAt && Date.now() - negAt < PREVIEW_NEGATIVE_TTL_MS) return null;

      const pending = inFlightPreview.current.get(videoId);
      if (pending) return pending;

      const run = fetchPreviewUrl(videoId, { token })
        .then((fresh) => {
          const ttlMs = (fresh.expires_in * 1000) * 0.9;
          qc.setQueryData(['video-preview', mediaCacheScope, videoId], {
            ...fresh,
            __expires_at: Date.now() + ttlMs,
          } as any);
          negativePreviewCache.current.delete(videoId);
          return fresh as PreviewUrlResponse | null;
        })
        .catch((err) => {
          if (err instanceof PreviewNotReadyError) {
            negativePreviewCache.current.set(videoId, Date.now());
            if (err.retryAfterMs !== undefined) {
              scheduleLazyPreviewRetry(videoId, err.retryAfterMs);
            }
            return null;
          }
          throw err;
        })
        .finally(() => {
          inFlightPreview.current.delete(videoId);
        });

      inFlightPreview.current.set(videoId, run);
      return run;
    },
    [mediaCacheScope, qc, token, items],
  );

  // ── Manual refresh ───────────────────────────────────────────────
  const refresh = useCallback(() => {
    return qc.invalidateQueries({ queryKey: QUERY_KEY });
  }, [qc]);

  // ── Delete (optimistic) ──────────────────────────────────────────
  // Single-flight per videoId so a double-click / re-fire doesn't issue
  // two DELETE requests against the same scope row.
  const inFlightDelete = useRef<Map<string, Promise<DeleteVideoResponse>>>(new Map());
  const [pendingDeletes, setPendingDeletes] = useState<ReadonlySet<string>>(() => new Set());

  const removeVideo = useCallback(
    async (videoId: string): Promise<DeleteVideoResponse> => {
      if (!videoId) throw new Error('videoId required');
      const existing = inFlightDelete.current.get(videoId);
      if (existing) return existing;

      // Optimistic: remove from cache immediately across every variant of
      // the query key (different `includeInitiated` toggles cache separate
      // entries). Restored from BFF refetch on settled.
      setPendingDeletes((s) => {
        const n = new Set(s); n.add(videoId); return n;
      });
      const snapshots = qc.getQueriesData<VideoListResponse>({ queryKey: QUERY_KEY });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<VideoListResponse>(key, {
          ...data,
          items: data.items.filter((it) => it.video_id !== videoId),
          total: Math.max(0, (data.total || 0) - 1),
        });
      }
      qc.removeQueries({ queryKey: ['video-playback', mediaCacheScope, videoId] });
      qc.removeQueries({ queryKey: ['video-detail', mediaCacheScope, videoId] });
      qc.removeQueries({ queryKey: ['video-poster', mediaCacheScope, videoId] });
      qc.removeQueries({ queryKey: ['video-preview', mediaCacheScope, videoId] });

      const run = deleteVideo(videoId)
        .catch((err) => {
          // Rollback on hard failure.
          for (const [key, data] of snapshots) {
            if (data) qc.setQueryData(key, data);
          }
          throw err;
        })
        .finally(() => {
          inFlightDelete.current.delete(videoId);
          setPendingDeletes((s) => {
            const n = new Set(s); n.delete(videoId); return n;
          });
          // Authoritative refresh — pulls the truthful tenant state and
          // also refreshes `uploaded-keys` consumers downstream (PACMobile).
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        });
      inFlightDelete.current.set(videoId, run);
      return run;
    },
    [mediaCacheScope, qc],
  );

  // ── Delete-all (tenant bulk; optimistic clear) ────────────────────
  // Single-flight: prevents accidental double-firing if the user
  // double-clicks the confirm button. Optimistic clear of the gallery
  // cache is rolled back if the BFF reports failure.
  const inFlightDeleteAll = useRef<Promise<DeleteAllVideosResponse> | null>(null);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const removeAllVideos = useCallback(
    async (confirm: string): Promise<DeleteAllVideosResponse> => {
      if (inFlightDeleteAll.current) return inFlightDeleteAll.current;

      setIsDeletingAll(true);
      const snapshots = qc.getQueriesData<VideoListResponse>({ queryKey: QUERY_KEY });
      // Optimistic clear across every cached variant.
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<VideoListResponse>(key, {
          ...data,
          items:      [],
          buckets:    [],
          total:      0,
          totalBytes: 0,
        });
      }
      qc.removeQueries({ queryKey: ['video-playback'] });
      qc.removeQueries({ queryKey: ['video-detail'] });

      const run = deleteAllVideos(confirm)
        .catch((err) => {
          // Rollback on hard failure — restore every snapshot.
          for (const [key, data] of snapshots) {
            if (data) qc.setQueryData(key, data);
          }
          throw err;
        })
        .finally(() => {
          inFlightDeleteAll.current = null;
          setIsDeletingAll(false);
          // Authoritative refresh — surfaces any residual rows
          // (failed deletes) so the user sees real state.
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        });

      inFlightDeleteAll.current = run;
      return run;
    },
    [qc],
  );

  // ── Reconcile (self-heal) ─────────────────────────────────────────
  // Single-flight per videoId for the same reason as deletes: prevents a
  // user double-clicking "Recover" from launching two server sweeps that
  // race on the same scope row. Optimistic UX: mark the row as `syncing`
  // (freshness chip) while the call is in flight, restore on settle.
  const inFlightRecover = useRef<Map<string, Promise<ReconcileSingleResponse>>>(new Map());
  const [pendingRecovers, setPendingRecovers] = useState<ReadonlySet<string>>(() => new Set());

  const recoverOne = useCallback(
    async (videoId: string): Promise<ReconcileSingleResponse> => {
      if (!videoId) throw new Error('videoId required');
      const existing = inFlightRecover.current.get(videoId);
      if (existing) return existing;

      setPendingRecovers((s) => { const n = new Set(s); n.add(videoId); return n; });
      // Optimistic freshness flip across every cached variant.
      const snapshots = qc.getQueriesData<VideoListResponse>({ queryKey: QUERY_KEY });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData<VideoListResponse>(key, {
          ...data,
          items: data.items.map((it) =>
            it.video_id === videoId ? { ...it, freshness: 'syncing' as VideoFeedItem['freshness'] } : it,
          ),
        });
      }

      const run = recoverVideo(videoId)
        .finally(() => {
          inFlightRecover.current.delete(videoId);
          setPendingRecovers((s) => { const n = new Set(s); n.delete(videoId); return n; });
          // Authoritative refresh — pulls server truth (status/workflow/freshness)
          // and replaces the optimistic syncing flag with whatever the BFF says.
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        });
      inFlightRecover.current.set(videoId, run);
      return run;
    },
    [qc],
  );

  // Bounded sweep across the tenant. Used by the admin "Reconcile all" button
  // and (debounced) auto-fired once on first paint when degraded rows are
  // detected, so the user doesn't have to discover the button to unstick a
  // long-stuck row from before this code shipped.
  const inFlightSweep = useRef<Promise<ReconcileSweepResponse> | null>(null);
  const [isReconciling, setIsReconciling] = useState(false);
  const [lastSweep, setLastSweep] = useState<ReconcileSweepResponse | null>(null);
  const [sweepError, setSweepError] = useState<string | null>(null);

  const reconcileSweep = useCallback(
    async (opts: { limit?: number; dryRun?: boolean } = {}): Promise<ReconcileSweepResponse> => {
      if (inFlightSweep.current) return inFlightSweep.current;
      setIsReconciling(true);
      setSweepError(null);
      const run = runReconcileSweep(opts)
        .then((res) => { setLastSweep(res); return res; })
        .catch((err) => {
          setSweepError((err as Error)?.message || 'reconcile_failed');
          throw err;
        })
        .finally(() => {
          inFlightSweep.current = null;
          setIsReconciling(false);
          qc.invalidateQueries({ queryKey: QUERY_KEY });
        });
      inFlightSweep.current = run;
      return run;
    },
    [qc],
  );

  // Auto-heal on first paint when REAL orphans are detected. Fires at most
  // once per mount.
  //
  // Gating signal: `classification.orphan_count > 0` — i.e. healable rows
  // with actual upload evidence. We deliberately do NOT trigger on a
  // generic `it.error` scan because in developer view phantoms surface
  // with `error='upload_orphan'` too, and sweeping over them is
  // guaranteed-fruitless work that wastes orchestrator capacity (and
  // produces 404 noise post-v1.0.19's pre-filter — the sweep walks a
  // different path than the list hydrator). Phantoms are an
  // orchestrator-internal cleanup concern, not a user-recoverable error.
  const autoSweepFiredRef = useRef(false);
  useEffect(() => {
    if (autoSweepFiredRef.current) return;
    if (query.isLoading || query.isFetching) return;
    if (classification.orphan_count <= 0) return;
    autoSweepFiredRef.current = true;
    // Fire-and-forget; sweepError state already captures failures for the UI.
    void reconcileSweep({ limit: 100 }).catch(() => { /* surfaced via sweepError */ });
  }, [classification.orphan_count, query.isLoading, query.isFetching, reconcileSweep]);

  // Operator-driven bulk orphan purge. Distinct from reconcile (which
  // tries to PROMOTE healable rows): purgeOrphans DELETES the unrecoverable
  // tail past TTL. Same single-flight + dry-run pattern as reconcile;
  // healthy rows are physically un-deletable because the server-side
  // decider gates on STATUS_SIDECARS_WRITTEN.
  const inFlightPurge = useRef<Promise<PurgeOrphansResponse> | null>(null);
  const [isPurgingOrphans, setIsPurgingOrphans] = useState(false);
  const [lastPurge, setLastPurge] = useState<PurgeOrphansResponse | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const purgeOrphansAction = useCallback(
    async (opts: { confirm: string; dryRun?: boolean; limit?: number }): Promise<PurgeOrphansResponse> => {
      if (inFlightPurge.current) return inFlightPurge.current;
      setIsPurgingOrphans(true);
      setPurgeError(null);
      const run = purgeOrphans(opts)
        .then((res) => { setLastPurge(res); return res; })
        .catch((err) => {
          setPurgeError((err as Error)?.message || 'purge_orphans_failed');
          throw err;
        })
        .finally(() => {
          inFlightPurge.current = null;
          setIsPurgingOrphans(false);
          // Only invalidate on live runs — dry-runs are read-only previews.
          if (!opts.dryRun) qc.invalidateQueries({ queryKey: QUERY_KEY });
        });
      inFlightPurge.current = run;
      return run;
    },
    [qc],
  );

  // Operator-driven sidecar backfill. Re-dispatches the existing faststart
  // worker across the legacy catalog so poster/preview sidecars can catch up.
  const inFlightBackfill = useRef<Promise<BackfillSidecarsResponse> | null>(null);
  const [isBackfillingSidecars, setIsBackfillingSidecars] = useState(false);
  const [lastBackfill, setLastBackfill] = useState<BackfillSidecarsResponse | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  const backfillSidecarsAction = useCallback(
    async (opts: { confirm: string; dryRun?: boolean; limit?: number }): Promise<BackfillSidecarsResponse> => {
      if (inFlightBackfill.current) return inFlightBackfill.current;
      setIsBackfillingSidecars(true);
      setBackfillError(null);
      const run = backfillSidecars(opts)
        .then((res) => { setLastBackfill(res); return res; })
        .catch((err) => {
          setBackfillError((err as Error)?.message || 'backfill_sidecars_failed');
          throw err;
        })
        .finally(() => {
          inFlightBackfill.current = null;
          setIsBackfillingSidecars(false);
          if (!opts.dryRun) qc.invalidateQueries({ queryKey: QUERY_KEY });
        });
      inFlightBackfill.current = run;
      return run;
    },
    [qc],
  );

  return {
    // server state
    items,
    buckets,
    classification,
    visibleItems,
    daysWithVideos,
    total:       query.data?.total ?? 0,
    totalBytes:  query.data?.totalBytes ?? 0,
    isLoading:   query.isLoading,
    isFetching:  query.isFetching,
    error:       query.error,
    freshness,
    degraded,
    servedFrom:  query.data?.served_from ?? null,
    lastUpdatedAt: query.dataUpdatedAt,

    // mode
    readOnly,
    token: token ?? null,

    // ui state
    activeBucketKey,
    setActiveBucketKey,
    searchText,
    setSearchText,
    includeInitiated,
    setIncludeInitiated,
    selectedDayMs,
    setSelectedDayMs,

    // actions
    refresh,
    requestDetail,
    requestPlayback,
    requestPoster,
    requestPreview,
    removeVideo,
    removeAllVideos,
    pendingDeletes,
    isDeletingAll,

    // reconcile (self-heal)
    recoverOne,
    reconcileSweep,
    pendingRecovers,
    isReconciling,
    lastSweep,
    sweepError,

    // purge (operator-driven orphan bulk-delete)
    purgeOrphans: purgeOrphansAction,
    isPurgingOrphans,
    lastPurge,
    purgeError,

    // backfill (operator-driven sidecar catch-up)
    backfillSidecars: backfillSidecarsAction,
    isBackfillingSidecars,
    lastBackfill,
    backfillError,
  };
}

export type UseVideoGalleryReturn = ReturnType<typeof useVideoGallery>;
